import React, { useState } from "react";
import { useForm } from "react-hook-form";
import InputsNew from "../common/InputsNew";
import SearchableInputsDropdown from "../common/SearchableDropdown";
import { format } from "date-fns";
import { useAuth } from "../../context/AuthContext";
import { ApolloError, useMutation } from "@apollo/client";
import { LEAVE_APPLY } from "../../api/mutation";
import Loader from "../common/Loader";
import { showError, showSuccess } from "../common/ToastNotification";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const leaveTypeOptions = [
    { key: 2, name: "Sick Leave" },
    { key: 3, name: "Casual Leave" },
    { key: 4, name: "Emergency Leave" },
    { key: 5, name: "Paternity Leave" },
    { key: 6, name: "Maternity Leave" },
]

interface LeaveFormData {
    leaveType: string;
    reason: string;
}

interface LeaveDetailsProps {
    selectedDates: { date: Date; isHalfDay: boolean }[];
}

const LeaveDetails: React.FC<LeaveDetailsProps> = ({ selectedDates }) => {
    const navigate = useNavigate();
    const { userInfo } = useAuth()
    const emplyeeID = userInfo?.employee_id

    const [dateError, setDateError] = useState("");

    // Validation schema
    const leaveDetailSchema = yup.object().shape({
        leaveType: yup.string()
            .test('not-only-whitespace', 'Please add a valid Leave Type', value => !value || value.trim().length > 0)
            .required('This field is required'),
        reason: yup.string()
            .test('not-only-whitespace', 'Please add a valid Reason', value => !value || value.trim().length > 0)
            .required('This field is required')
    });

    //api
    const [leaveApply, { loading: leaveApplyLoading }] = useMutation(LEAVE_APPLY, {
        fetchPolicy: 'no-cache',
    })

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        setValue
    } = useForm<LeaveFormData>({
        mode: "onChange",
        resolver: yupResolver(leaveDetailSchema)
    });

    async function onSubmit(data: LeaveFormData) {
        if (selectedDates.length === 0) {
            setDateError("Please select at least one date.");
            return;
        }
        setDateError("");

        // const leaves = selectedDates.map((sd: Date) => {
        //     return {
        //         start_date: `${format(sd, "yyyy-MM-dd")}T00:00:00`,
        //         end_date: `${format(sd, "yyyy-MM-dd")}T23:59:59`,
        //     };
        // })

        const leaves = selectedDates.map((sd) => ({
            start_date: `${format(sd.date, "yyyy-MM-dd")}T00:00:00`,
            end_date: `${format(sd.date, "yyyy-MM-dd")}T23:59:59`,
            is_half_day: sd.isHalfDay
        }));

        const input = {
            leaves: leaves,
            employee_id: emplyeeID,
            leave_type: data.leaveType,
            reason: data.reason,
            status: "Pending",
            created_by: emplyeeID
        }

        try {
            const response = await leaveApply({ variables: { input: input } });
            if (response.data.leave_apply.status_code === 200) {
                showSuccess(response.data.leave_apply.message)
                navigate(-1)
            }
            if (response.data.leave_apply.status_code === 400) {
                showError(response.data.leave_apply.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    };

    return (
        <>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="bg-white p-4 border-[1px] border-solid border-surface-border grid gap-3 leave_details_form"
            >
                <h2 className="font-semibold my-0">Leave Details</h2>

                {/* Date Selection Error */}
                {dateError && <span className="text-red-500 text-sm">{dateError}</span>}

                {/* Leave Type Dropdown */}
                <SearchableInputsDropdown
                    label="Leave Type*"
                    placeHolder="--Select--"
                    options={leaveTypeOptions}
                    onSelect={(key: number, name: string) => {
                        setValue('leaveType', name);
                    }}
                    errorMessage={errors?.leaveType}
                />

                {/* Reason Textarea */}
                <InputsNew
                    contentPlaceholder="Reason for leave"
                    label="Reason*"
                    type="text"
                    register={register('reason')}
                    errorMessage={errors.reason}
                />

                {/* Submit Button */}
                <div className="self-stretch flex items-end justify-end mq950:items-center mq950:justify-end mq950:pb-10 mq950:box-border gap-[12px] ">
                     <button
                    type="submit"
                    className={`rounded  kpi_active_dv cursor-pointer [border:none] px-2 py-4 flex-1 rounded-lg flex flex-col items-center justify-center gap-[8px] green-clockin-button-item text-text-text-tertiary hover-kpi_active_dv mq450:w-[calc(100%_-_40px)] ${isValid ? "" : ""
                        }`}
                >
                    Submit Leave Request
                </button>
                </div>
               
            </form>
            {leaveApplyLoading && (
                <Loader />
            )}
        </>
    );
};

export default LeaveDetails;
